import { AxiosRequestConfig, AxiosPromise, AxiosResponse } from '../types'
import { parseHeaders } from '../helpers/headers'
import { createError } from '../helpers/error'
import { isURLSameOrigin } from '../helpers/url'
import { isFormData } from '../helpers/util'
import cookie from '../helpers/cookie'

export default function xhr(config: AxiosRequestConfig): AxiosPromise {
  return new Promise((resolve, reject) => {
    const {
      data = null,
      url,
      method,
      headers = {},
      responseType,
      timeout,
      cancelToken,
      withCredentials,
      xsrfHeaderName,
      xsrfCookieName,
      onDownloadProgress,
      onUploadProgress,
      auth,
      validateStatus
    } = config

    const request = new XMLHttpRequest() // 1. 创建request实例
    request.open(method!.toUpperCase(), url!, true) // 2.执行 request.open 方法初始化
    configureRequest() // 3.执行 configureRequest 配置 request 对象。
    addEvents() // 4.执行 addEvents 给 request 添加事件处理函数。
    processHeaders() // 5.执行 processHeaders 处理请求 headers。
    processCancel() // 6.执行 processCancel 处理请求取消逻辑。
    request.send(data) // 7.执行 request.send 方法发送请求。

    // config处理
    function configureRequest(): void {
      if (responseType) {
        request.responseType = responseType
      }

      // 超时时间设置
      if (timeout) {
        request.timeout = timeout
      }

      // 允许跨域请求时携带cookie
      if (withCredentials) {
        request.withCredentials = withCredentials
      }
    }

    // 添加事件函数处理
    function addEvents(): void {
      // 当状态改变时
      request.onreadystatechange = function handleLoad() {
        // 如果状态是4 就能够正确获取到返回值
        if (request.readyState !== 4) return

        // 当网络错误或是超时，status 都是0
        if (request.status === 0) return

        const responseHeaders = parseHeaders(request.getAllResponseHeaders()) // 获取到response 的headers
        const responseData =
          responseType && responseType !== 'text' ? request.response : request.responseText // 根据设置来返回data值
        const response: AxiosResponse = {
          data: responseData,
          status: request.status,
          statusText: request.statusText,
          headers: responseHeaders,
          config,
          request
        }
        handleResponse(response)
      }

      // 网络错误处理
      request.onerror = function handleError() {
        reject(createError('Network Error', config, null, request))
      }

      // 超时时间处理
      request.ontimeout = function handleTimeout() {
        reject(createError(`Timeout of ${timeout} ms exceeded`, config, 'ECONNABORTED', request))
      }

      // 监控下载事件
      if (onDownloadProgress) {
        request.onprogress = onDownloadProgress
      }

      // 监控上传事件
      if (onUploadProgress) {
        request.upload.onprogress = onUploadProgress
      }
    }

    // 处理headers
    function processHeaders(): void {
      // 如果请求的数据是 FormData 类型，我们应该主动删除请求 headers 中的 Content-Type 字段，让浏览器自动根据请求数据设置 Content-Type。
      if (isFormData(data)) {
        delete headers['Content-Type']
      }

      // 在请求header 中携带token
      if ((withCredentials || isURLSameOrigin(url!)) && xsrfCookieName) {
        const xsrfValue = cookie.read(xsrfCookieName)
        if (xsrfValue && xsrfHeaderName) {
          headers[xsrfHeaderName] = xsrfValue
        }
      }

      // 处理设置了auth, header自动添加Authorization(值wei Basic (username + password) 的base64码)
      if (auth) {
        headers['Authorization'] = 'Basic ' + btoa(auth.username + ':' + auth.password)
      }

      // 给header 设置对应的值
      Object.keys(headers).forEach(name => {
        // 当data没有值得时候，设置Content-Type 是没有意义的，因此可以删除Content-Type
        if (data === null && name.toLowerCase() === 'content-type') {
          delete headers[name]
        } else {
          request.setRequestHeader(name, headers[name])
        }
      })
    }

    // 处理cancel
    function processCancel(): void {
      // 处理是否取消
      if (cancelToken) {
        cancelToken.promise
          .then(reason => {
            request.abort() // 取消请求
            reject(reason) // 将取消原因抛出去
          })
          .catch(() => {
            /* istanbul ignore next */
            // do nothing
          })
      }
    }

    // 处理返回值错误的时候
    function handleResponse(response: AxiosResponse): void {
      if (!validateStatus || validateStatus(response.status)) {
        resolve(response)
      } else {
        reject(
          createError(
            `Request failed with status code ${response.status}`,
            config,
            null,
            request,
            response
          )
        )
      }
    }
  })
}
